"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { ChevronLeft, ChevronRight, Eye, FileText } from "lucide-react";
import { FileItemIcon } from "@/components/files/FileItemIcon";
import type { FileCategoryNode, FileEntryItem } from "@/lib/files/server";
import { countAvailableInSubtree } from "./counts";
import type {
  FileCategoryLayoutProps,
  FilesDirectoryLayoutProps,
} from "./types";

const THEME = "var(--association-theme-color-1)";
const PAGE_SIZES = [12, 24, 48];

function CompactNav({
  tree,
  locale,
  activeSlug,
  fileCounts,
  title,
}: {
  tree: FileCategoryNode[];
  locale: string;
  activeSlug?: string;
  fileCounts: Record<string, number>;
  title: string;
}) {
  return (
    <nav
      aria-label={title}
      className="overflow-hidden rounded-xl border border-border/80 bg-card shadow-sm"
    >
      <ul>
        {tree.map((node) => {
          const active = node.slug === activeSlug;
          const count = countAvailableInSubtree(node, fileCounts);
          return (
            <li
              key={node.id}
              className="border-b border-border/70 last:border-b-0"
            >
              <Link
                href={`/${locale}/files/${node.slug}`}
                className={`relative flex items-center gap-3 px-4 py-3.5 transition-colors ${
                  active ? "" : "hover:bg-muted/50"
                }`}
              >
                {active ? (
                  <span
                    aria-hidden
                    className="absolute inset-y-1.5 start-0 w-1 rounded-full"
                    style={{ backgroundColor: THEME }}
                  />
                ) : null}
                <span
                  className={`shrink-0 ${active ? "" : "text-muted-foreground"}`}
                  style={active ? { color: THEME } : undefined}
                >
                  <FileText className="h-4 w-4" aria-hidden />
                </span>
                <span
                  className={`min-w-0 flex-1 truncate text-sm font-medium ${
                    active ? "" : "text-foreground/80"
                  }`}
                  style={active ? { color: THEME } : undefined}
                >
                  {node.title}
                </span>
                <span className="flex h-7 min-w-7 shrink-0 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-semibold text-foreground/70">
                  {count}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

function CompactFileCard({
  file,
  isAr,
}: {
  file: FileEntryItem;
  isAr: boolean;
}) {
  const available = file.isAvailable === true && Boolean(file.fileUrl);
  return (
    <article
      className={`flex h-full flex-col rounded-xl border border-border/80 bg-card p-4 shadow-sm ${
        available
          ? "transition hover:border-[color-mix(in_srgb,var(--association-theme-color-1)_40%,transparent)] hover:shadow-md"
          : "opacity-80"
      }`}
    >
      <div className="flex items-start justify-between !items-center gap-2">
        <span style={{ color: available ? THEME : undefined }} className={available ? "" : "text-muted-foreground"}>
          <FileItemIcon className="h-12 w-12" />
        </span>
        {available ? (
          <a
            href={file.fileUrl!}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-foreground"
          >
            <Eye className="h-3.5 w-3.5" aria-hidden />
            {isAr ? "عرض الملف" : "View file"}
          </a>
        ) : null}
      </div>
      <h3
        className={`mt-3 line-clamp-2 text-sm font-semibold leading-relaxed tracking-tight ${
          available ? "" : "text-muted-foreground"
        }`}
      >
        {file.title}
      </h3>
    </article>
  );
}

/** Page numbers with an ellipsis window, e.g. 1 2 3 … 8 */
function pageItems(current: number, total: number): Array<number | "gap"> {
  if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, "gap", total];
  if (current >= total - 2) return [1, "gap", total - 2, total - 1, total];
  return [1, "gap", current, "gap", total];
}

function Pagination({
  page,
  pageCount,
  onPage,
  pageSize,
  onPageSize,
  isAr,
}: {
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
  pageSize: number;
  onPageSize: (s: number) => void;
  isAr: boolean;
}) {
  const navBtn =
    "flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-card text-foreground/70 transition hover:bg-muted disabled:opacity-40 disabled:hover:bg-card";

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-label={isAr ? "الصفحة السابقة" : "Previous page"}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className={navBtn}
        >
          <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
        </button>
        {pageItems(page, pageCount).map((item, idx) =>
          item === "gap" ? (
            <span key={`gap-${idx}`} className="px-1 text-muted-foreground">
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-current={item === page ? "page" : undefined}
              onClick={() => onPage(item)}
              className={`h-8 min-w-8 rounded-lg px-2 text-sm font-semibold transition ${
                item === page
                  ? "text-white shadow-sm"
                  : "text-foreground/70 hover:bg-muted"
              }`}
              style={item === page ? { backgroundColor: THEME } : undefined}
            >
              {item}
            </button>
          ),
        )}
        <button
          type="button"
          aria-label={isAr ? "الصفحة التالية" : "Next page"}
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
          className={navBtn}
        >
          <ChevronRight className="h-4 w-4 rtl:rotate-180" />
        </button>
      </div>

      <label className="flex items-center gap-2 text-sm text-muted-foreground">
        {isAr ? "عرض لكل صفحة" : "Per page"}
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          className="rounded-lg border border-border bg-card px-2 py-1 text-sm text-foreground outline-none"
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </label>
    </div>
  );
}

function PaginatedFileGrid({
  files,
  isAr,
}: {
  files: FileEntryItem[];
  isAr: boolean;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]!);

  useEffect(() => {
    setPage(1);
  }, [files, pageSize]);

  const pageCount = Math.max(1, Math.ceil(files.length / pageSize));
  const visible = useMemo(
    () => files.slice((page - 1) * pageSize, page * pageSize),
    [files, page, pageSize],
  );

  if (files.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center text-muted-foreground">
        {isAr ? "لا توجد ملفات." : "No files found."}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {visible.map((file) => (
          <li key={file.id}>
            <CompactFileCard file={file} isAr={isAr} />
          </li>
        ))}
      </ul>
      {pageCount > 1 || files.length > PAGE_SIZES[0]! ? (
        <Pagination
          page={page}
          pageCount={pageCount}
          onPage={setPage}
          pageSize={pageSize}
          onPageSize={setPageSize}
          isAr={isAr}
        />
      ) : null}
    </div>
  );
}

export function CompactSidebarDirectoryLayout({
  tree,
  locale,
  isAr,
  fileCounts,
}: FilesDirectoryLayoutProps) {
  return (
    <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-10">
      <aside className="min-w-0">
        <div className="lg:sticky lg:top-24">
          <CompactNav
            tree={tree}
            locale={locale}
            fileCounts={fileCounts}
            title={isAr ? "التصنيفات" : "Categories"}
          />
        </div>
      </aside>
      <section className="min-w-0">
        <p className="mb-6 max-w-3xl text-base leading-relaxed text-muted-foreground">
          {isAr
            ? "اختر تصنيفاً من القائمة لعرض الملفات."
            : "Choose a category from the list to view files."}
        </p>
        {tree.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center text-muted-foreground">
            {isAr ? "لا توجد تصنيفات بعد." : "No categories yet."}
          </div>
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {tree.map((node) => {
              const count = countAvailableInSubtree(node, fileCounts);
              return (
                <li key={node.id}>
                  <Link
                    href={`/${locale}/files/${node.slug}`}
                    className="flex items-center gap-3 rounded-xl border border-border/80 bg-card p-4 shadow-sm transition hover:border-[color-mix(in_srgb,var(--association-theme-color-1)_40%,transparent)]"
                  >
                    <span className="shrink-0" style={{ color: THEME }}>
                      <FileItemIcon className="h-11 w-11" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">
                        {node.title}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {isAr ? `${count} ملف` : `${count} files`}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}

export function CompactSidebarCategoryLayout({
  tree,
  locale,
  isAr,
  fileCounts,
  activeSlug,
  category,
  files,
  childFiles,
  activeNode,
}: FileCategoryLayoutProps) {
  const children = activeNode?.children ?? [];
  const hasChildren = children.length > 0;
  const firstChildSlug = hasChildren ? children[0]!.slug : activeSlug;
  const [tabSlug, setTabSlug] = useState<string>(firstChildSlug);

  useEffect(() => {
    setTabSlug(firstChildSlug);
  }, [activeSlug, firstChildSlug]);

  const displayedFiles = hasChildren ? (childFiles[tabSlug] ?? []) : files;
  const totalCount = activeNode
    ? countAvailableInSubtree(activeNode, fileCounts)
    : files.filter((f) => f.isAvailable && f.fileUrl).length;

  return (
    <div className="grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-10">
      <aside className="min-w-0">
        <div className="lg:sticky lg:top-24">
          <CompactNav
            tree={tree}
            locale={locale}
            activeSlug={activeSlug}
            fileCounts={fileCounts}
            title={isAr ? "التصنيفات" : "Categories"}
          />
        </div>
      </aside>
      <section className="min-w-0 space-y-6">
        <h2 className="text-xl font-bold tracking-tight">
          {category.title}
          <span className="ms-1.5 font-bold">({totalCount})</span>
        </h2>

        {hasChildren ? (
          <div
            role="tablist"
            className="flex flex-wrap gap-x-7 gap-y-2 border-b border-border/70"
          >
            {children.map((child) => {
              const selected = tabSlug === child.slug;
              return (
                <button
                  key={child.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setTabSlug(child.slug)}
                  className={`-mb-px border-b-2 pb-2.5 text-sm transition-colors ${
                    selected
                      ? "font-bold text-foreground"
                      : "border-transparent font-medium text-muted-foreground hover:text-foreground"
                  }`}
                  style={selected ? { borderBottomColor: THEME } : undefined}
                >
                  {child.title}
                </button>
              );
            })}
          </div>
        ) : null}

        <PaginatedFileGrid files={displayedFiles} isAr={isAr} />
      </section>
    </div>
  );
}
